import { DoqType, DoqWithSpeechBubble } from '@ardoq/doq';
import { ReportError } from './types';
import { useReportTracking } from './tracking/ReportTrackingContext';
import { ArdoqError, isArdoqError } from '@ardoq/common-helpers';
import {
  EmptyState,
  EmptyStateNotFoundIllustration,
  ErrorState,
} from '@ardoq/status-ui';
import { PrimaryButton } from '@ardoq/button';
import { FlexBox } from '@ardoq/layout';
import type { ErrorPageLabels } from './labels';
import type { I18nLocale } from '@ardoq/i18n';

export type { ErrorPageLabels };

const DEFAULT_LABELS = {
  restrictedTitle: 'This report is restricted',
  restrictedMessage:
    'Only users with Gremlin privilege can view and edit Gremlin based reports, regardless of other report permissions.',
  noAccessTitle: 'No access to report',
  noReadAccessMessage: "You don't have access to view the report",
  noEditAccessMessage: "You don't have access to edit the report",
  goBackHomeButton: 'Go back home',
  missingWorkspaceAccessTitle: "You can't edit this report",
  missingWorkspaceAccessDescription:
    "You don't have access to edit this report, but you can still open and view it.",
  viewReportButton: 'View report',
  reportFailedTitle: 'Report failed',
  traceIdLabel: 'Trace ID',
  reportNotFoundTitle: 'Report not found',
  reportNotFoundMessage:
    'The report you are looking for does not exist. Contact your Ardoq admin for assistance.',
} satisfies Required<ErrorPageLabels>;

const Errors = {
  missingOrgErrors: [
    ReportError.USER_IS_MISSING_WORKSPACE_ACCESS,
    ReportError.USER_IS_MISSING_READ_ACCESS_TO_REPORT,
    ReportError.USER_IS_MISSING_EDIT_ACCESS_TO_REPORT,
  ],
  goHomeErrors: [
    ReportError.USER_IS_MISSING_READ_ACCESS_TO_REPORT,
    ReportError.USER_IS_MISSING_EDIT_ACCESS_TO_REPORT,
    ReportError.REPORT_NOT_FOUND,
  ],
  noReportAccessErrors: [
    ReportError.USER_IS_MISSING_READ_ACCESS_TO_REPORT,
    ReportError.USER_IS_MISSING_EDIT_ACCESS_TO_REPORT,
  ],
};

const getTitle = (
  error: ReportError,
  labels: Required<ErrorPageLabels>
): string | undefined => {
  if (
    error === ReportError.USER_CANNOT_CREATE_GREMLIN_AND_DATA_SOURCE_IS_GREMLIN
  ) {
    return labels.restrictedTitle;
  } else if (error === ReportError.REPORT_NOT_FOUND) {
    return labels.reportNotFoundTitle;
  } else if (Errors.noReportAccessErrors.includes(error)) {
    return labels.noAccessTitle;
  }
};

const getErrorMessage = (
  error: ReportError,
  labels: Required<ErrorPageLabels>
): string | undefined => {
  if (
    error === ReportError.USER_CANNOT_CREATE_GREMLIN_AND_DATA_SOURCE_IS_GREMLIN
  ) {
    return labels.restrictedMessage;
  } else if (error === ReportError.USER_IS_MISSING_READ_ACCESS_TO_REPORT) {
    return labels.noReadAccessMessage;
  } else if (error === ReportError.USER_IS_MISSING_EDIT_ACCESS_TO_REPORT) {
    return labels.noEditAccessMessage;
  } else if (error === ReportError.REPORT_NOT_FOUND) {
    return labels.reportNotFoundMessage;
  }
};

const getDoqType = (error: ReportError) => {
  if (
    error === ReportError.USER_CANNOT_CREATE_GREMLIN_AND_DATA_SOURCE_IS_GREMLIN
  ) {
    return DoqType.RELATIONSHIPS_ERROR;
  } else if (error === ReportError.REPORT_NOT_FOUND) {
    return DoqType.PAGE_GONE;
  } else if (Errors.missingOrgErrors.includes(error)) {
    return DoqType.MISSING_ORG;
  }
};

const getButtonLabel = (
  error: ReportError,
  labels: Required<ErrorPageLabels>
): string | undefined => {
  if (Errors.goHomeErrors.includes(error)) {
    return labels.goBackHomeButton;
  }
};

interface ErrorPageProps {
  error: ReportError | ArdoqError;
  openHome?: VoidFunction;
  viewReport?: VoidFunction;
  labels?: ErrorPageLabels;
  i18nLocale: I18nLocale;
}

export const ErrorPage = ({
  error,
  openHome,
  viewReport,
  labels,
  i18nLocale,
}: ErrorPageProps) => {
  const mergedLabels = { ...DEFAULT_LABELS, ...labels };
  const { trackMissingReadAccess } = useReportTracking();
  if (error === ReportError.USER_IS_MISSING_READ_ACCESS_TO_REPORT)
    trackMissingReadAccess();

  return (
    <FlexBox justify={'center'} align={'center'} height={'full'}>
      {error === ReportError.USER_IS_MISSING_WORKSPACE_ACCESS ? (
        <EmptyState
          media={<EmptyStateNotFoundIllustration />}
          title={mergedLabels.missingWorkspaceAccessTitle}
          description={mergedLabels.missingWorkspaceAccessDescription}
        >
          <PrimaryButton onClick={viewReport}>
            {mergedLabels.viewReportButton}
          </PrimaryButton>
        </EmptyState>
      ) : isArdoqError(error) ? (
        <ErrorState
          layout="vertical"
          role="alert"
          title={mergedLabels.reportFailedTitle}
          error={error}
          i18nLocale={i18nLocale}
        />
      ) : (
        <DoqWithSpeechBubble
          title={getTitle(error, mergedLabels)}
          doqType={getDoqType(error)}
          message={getErrorMessage(error, mergedLabels)}
          buttonLabel={getButtonLabel(error, mergedLabels)}
          onButtonClick={
            Errors.goHomeErrors.includes(error) ? openHome : undefined
          }
        />
      )}
    </FlexBox>
  );
};
